import { useMemo } from 'react';
import { VALUE_CHAIN_MONITORING_AGENT } from 'constants/general';
import {
  isClimateMonitoringSignalResult,
  isFinanceMonitoringSignalResult,
  isOwnershipMonitoringSignalResult,
  isRegulationMonitoringSignalResult,
  isReputationMonitoringSignalResult,
} from 'constants/interfaces/IMonitoringSignalResult';
import { useGetMonitoringResultsQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import {
  buildFallbackClimateResults,
  buildFallbackFinanceResults,
  buildFallbackMonitoringResults,
  buildFallbackOwnershipResults,
  buildFallbackRegulationResults,
} from '../utils/monitoringResultsUtils';

import type { TMonitoringResultsMode } from 'constants/interfaces/IMonitoringAgentDefinition';
import type { TMonitoringAnySignalResult } from 'constants/interfaces/IMonitoringSignalResult';

interface IUseMonitoringResultsParams {
  valueChainMapId?: number,
  sessionId?: number,
  runId?: string,
  isEnabled: boolean,
  resultsMode?: TMonitoringResultsMode,
  fallbackEntities?: { unitId: number, name: string }[],
}

interface IUseMonitoringResultsReturn {
  results: TMonitoringAnySignalResult | null,
  isLoading: boolean,
  isError: boolean,
  isEmpty: boolean,
}

const useMonitoringResults = ({
  valueChainMapId,
  sessionId,
  runId,
  isEnabled,
  resultsMode = 'articles',
  fallbackEntities = [],
}: IUseMonitoringResultsParams): IUseMonitoringResultsReturn => {
  const hasValidMapId = Number.isInteger(valueChainMapId) && (valueChainMapId ?? 0) > 0;
  const hasResultKey = Boolean(runId) || Number.isInteger(sessionId);
  const shouldFetch = isEnabled && hasValidMapId && hasResultKey;
  const isRegulationMode = resultsMode === 'regulations';
  const isFinanceMode = resultsMode === 'financial-risk';
  const isOwnershipMode = resultsMode === 'ownership';
  const isClimateMode = resultsMode === 'climate';

  const {
    data: response,
    isLoading,
    isError,
  } = useGetMonitoringResultsQuery(
    {
      valueChainMapId: valueChainMapId as number,
      sessionId,
      runId,
    },
    { skip: !shouldFetch },
  );

  const results = useMemo<TMonitoringAnySignalResult | null>(() => {
    const apiResults = response?.data ?? null;

    if (isFinanceMode) {
      if (
        isFinanceMonitoringSignalResult(apiResults)
        && (apiResults.findings.length || apiResults.entities.length)
      ) {
        return apiResults;
      }

      if (
        VALUE_CHAIN_MONITORING_AGENT.FINANCE_MONITORING_USE_MOCK
        && fallbackEntities.length > 0
      ) {
        return buildFallbackFinanceResults(fallbackEntities);
      }

      return isFinanceMonitoringSignalResult(apiResults) ? apiResults : null;
    }

    if (isRegulationMode) {
      if (
        isRegulationMonitoringSignalResult(apiResults)
        && apiResults.regulations_found.length
      ) {
        return apiResults;
      }

      if (
        VALUE_CHAIN_MONITORING_AGENT.REGULATION_MONITORING_USE_MOCK
        && fallbackEntities.length > 0
      ) {
        return buildFallbackRegulationResults(fallbackEntities);
      }

      return isRegulationMonitoringSignalResult(apiResults) ? apiResults : null;
    }

    if (isOwnershipMode) {
      if (
        isOwnershipMonitoringSignalResult(apiResults)
        && (apiResults.findings.length || apiResults.entities.length)
      ) {
        return apiResults;
      }

      if (
        VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_MONITORING_USE_MOCK
        && fallbackEntities.length > 0
      ) {
        return buildFallbackOwnershipResults(fallbackEntities);
      }

      return isOwnershipMonitoringSignalResult(apiResults) ? apiResults : null;
    }

    if (isClimateMode) {
      if (
        isClimateMonitoringSignalResult(apiResults)
        && (apiResults.findings.length || apiResults.entities.length)
      ) {
        return apiResults;
      }

      if (
        VALUE_CHAIN_MONITORING_AGENT.CLIMATE_MONITORING_USE_MOCK
        && fallbackEntities.length > 0
      ) {
        return buildFallbackClimateResults(fallbackEntities);
      }

      return isClimateMonitoringSignalResult(apiResults) ? apiResults : null;
    }

    if (isReputationMonitoringSignalResult(apiResults) && apiResults.articles_found.length) {
      return apiResults;
    }

    if (
      VALUE_CHAIN_MONITORING_AGENT.REPUTATION_MONITORING_USE_MOCK
      && fallbackEntities.length > 0
    ) {
      return buildFallbackMonitoringResults(fallbackEntities);
    }

    return isReputationMonitoringSignalResult(apiResults) ? apiResults : null;
  }, [fallbackEntities, isClimateMode, isFinanceMode, isOwnershipMode, isRegulationMode, response?.data]);

  const isEmpty = (() => {
    if (isLoading) {
      return false;
    }

    if (isFinanceMode) {
      return !isFinanceMonitoringSignalResult(results)
        || (!results.findings.length && !results.entities.length);
    }

    if (isRegulationMode) {
      return !isRegulationMonitoringSignalResult(results) || !results.regulations_found.length;
    }

    if (isOwnershipMode) {
      return !isOwnershipMonitoringSignalResult(results)
        || (!results.findings.length && !results.entities.length);
    }

    if (isClimateMode) {
      return !isClimateMonitoringSignalResult(results)
        || (!results.findings.length && !results.entities.length);
    }

    return !isReputationMonitoringSignalResult(results) || !results.articles_found.length;
  })();

  return {
    results,
    isLoading: shouldFetch && isLoading,
    isError,
    isEmpty,
  };
};

export default useMonitoringResults;
